import { useState, useEffect } from "react";
import { NavLink, Link, useNavigate, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Users, Briefcase, Package, MessageSquare, Sparkles,
  LogOut, Loader2, Inbox, Receipt, Trophy, Layers, Droplet, PackageOpen,
  ShieldCheck, Gift, FileText, Mail, Menu, X, Store, BarChart3,
  Palette, HelpCircle,
} from "lucide-react";
import { FlowtalisWordmark } from "@/components/layout/Header";
import NotificationBell from "@/components/NotificationBell";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/leads", label: "Leads", icon: Inbox },
  { to: "/admin/samples", label: "Sample Requests", icon: PackageOpen },
  { to: "/admin/sample-kits", label: "Sample Kits", icon: Gift },
  { to: "/admin/clients", label: "Clients", icon: Users },
  { to: "/admin/projects", label: "Projects", icon: Briefcase },
  { to: "/admin/showcase", label: "Showcase", icon: Trophy },
  { to: "/admin/turnkey-brands", label: "Turnkey Brands", icon: Store },
  { to: "/admin/turnkey-inquiries", label: "Turnkey Inquiries", icon: Inbox },
  { to: "/admin/turnkey-analytics", label: "Turnkey Analytics", icon: BarChart3 },
  { to: "/admin/reorder-requests", label: "Reorder Requests", icon: PackageOpen },
  { to: "/admin/artwork", label: "Artwork Center", icon: Palette },
  { to: "/admin/vault", label: "Brand Vault", icon: Package },
  { to: "/admin/support", label: "Support Center", icon: HelpCircle },
  { to: "/admin/collections", label: "Collections", icon: Layers },
  { to: "/admin/bottle-sizes", label: "Bottle Sizes", icon: Droplet },
  { to: "/admin/compliance-badges", label: "Compliance Badges", icon: ShieldCheck },
  { to: "/admin/trust-documents", label: "Trust Documents", icon: FileText },
  { to: "/admin/invoices", label: "Finance", icon: Receipt },
  { to: "/admin/products", label: "Products", icon: Package },
  { to: "/admin/contacts", label: "Contact Requests", icon: MessageSquare },
  { to: "/admin/email-log", label: "Email Log", icon: Mail },
  { to: "/admin/ai", label: "AI Assistant", icon: Sparkles },
];

// SidebarBody — shared between the fixed desktop <aside> and the mobile
// slide-in drawer so a single source of truth drives both surfaces.
const SidebarBody = ({ user, onLinkClick, onSignOut }) => (
  <>
    <Link to="/" className="h-16 flex items-center px-6 border-b border-white/8" onClick={onLinkClick}>
      <span
        className="font-bold tracking-[0.22em] text-[1.1rem] leading-none"
        style={{ color: "var(--ft-gold)", fontFamily: "Manrope, sans-serif" }}
      >
        FLOWTALIS<span className="text-[0.55em] align-super ml-0.5 font-medium">™</span>
      </span>
    </Link>
    <div className="px-6 py-3 border-b border-white/8">
      <div className="text-[0.625rem] uppercase tracking-[0.28em] text-[var(--ft-gold)] font-semibold">Enterprise Console</div>
    </div>
    <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
      {NAV.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          onClick={onLinkClick}
          data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
          className={({ isActive }) => `ft-side-link ${isActive ? "active" : ""}`}
        >
          <item.icon size={18} /> {item.label}
        </NavLink>
      ))}
    </nav>
    <div className="p-4 border-t border-white/8 text-white">
      <div className="text-[0.625rem] text-white/40 mb-2 uppercase tracking-[0.24em] font-semibold">
        {user.role.replace("_", " ")}
      </div>
      <div className="text-sm font-semibold truncate">{user.full_name}</div>
      <button
        onClick={onSignOut}
        data-testid="admin-logout"
        className="mt-3 flex items-center gap-2 text-sm text-white/60 hover:text-[var(--ft-gold)] transition-colors min-h-[44px]"
      >
        <LogOut size={16} /> Sign out
      </button>
    </div>
  </>
);

const AdminLayout = ({ children }) => {
  const { user, loading, isAdmin, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Close the drawer whenever the route changes — otherwise tapping a nav
  // link leaves the overlay hanging over the page a user just navigated to.
  useEffect(() => { setDrawerOpen(false); }, [loc.pathname]);

  // Lock body scroll while the drawer is open so the underlying page can't
  // scroll behind the overlay on iOS Safari.
  useEffect(() => {
    if (drawerOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [drawerOpen]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin" /></div>;
  if (!user) return <Navigate to="/login" state={{ from: loc.pathname }} replace />;
  if (!isAdmin) return <Navigate to="/portal" replace />;

  const signOut = () => { logout(); nav("/"); };

  return (
    <div className="min-h-screen bg-[var(--ft-soft-gray)] flex overflow-x-hidden">
      {/* ---- Desktop fixed sidebar (>=768px) ---------------------------- */}
      <aside
        className="ft-sidebar w-64 hidden md:flex md:flex-col fixed inset-y-0 left-0 z-30"
        style={{ background: "var(--ft-black)", borderRight: "1px solid rgba(255,255,255,0.08)" }}
      >
        <SidebarBody user={user} onSignOut={signOut} />
      </aside>

      {/* ---- Mobile slide-in drawer (<768px) ---------------------------- */}
      {drawerOpen && (
        <button
          onClick={() => setDrawerOpen(false)}
          aria-label="Close menu"
          className="md:hidden fixed inset-0 bg-black/50 z-40 backdrop-blur-sm animate-in fade-in duration-200"
          data-testid="admin-drawer-scrim"
        />
      )}
      <aside
        className={`ft-sidebar md:hidden flex flex-col fixed inset-y-0 left-0 z-50 w-[85vw] max-w-[320px] transform transition-transform duration-300 ease-out ${
          drawerOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        style={{ background: "var(--ft-black)", borderRight: "1px solid rgba(255,255,255,0.08)" }}
        data-testid="admin-drawer"
        aria-hidden={!drawerOpen}
      >
        <SidebarBody user={user} onLinkClick={() => setDrawerOpen(false)} onSignOut={signOut} />
      </aside>

      {/* ---- Main content column ---------------------------------------- */}
      <div className="flex-1 min-w-0 md:ml-64">
        {/* Mobile top bar with hamburger */}
        <div className="md:hidden h-16 border-b border-[var(--ft-border)] bg-white flex items-center justify-between px-4 sticky top-0 z-20">
          <button
            onClick={() => setDrawerOpen(true)}
            aria-label="Open menu"
            data-testid="admin-drawer-toggle"
            className="h-11 w-11 -ml-1 flex items-center justify-center rounded-lg hover:bg-[var(--ft-light-gray)] transition-colors"
          >
            <Menu size={22} strokeWidth={1.75} />
          </button>
          <FlowtalisWordmark variant="light" />
          <div className="flex items-center gap-1">
            <NotificationBell />
            <button
              onClick={signOut}
              aria-label="Sign out"
              className="h-11 w-11 -mr-1 flex items-center justify-center rounded-lg hover:bg-[var(--ft-light-gray)] transition-colors"
              data-testid="admin-mobile-logout"
            >
              <LogOut size={18} strokeWidth={1.75} />
            </button>
          </div>
        </div>

        {/* Desktop notification bell — floats top-right of content */}
        <div className="hidden md:flex justify-end px-6 pt-4">
          <NotificationBell />
        </div>

        <main className="p-4 sm:p-6 md:p-10 min-w-0">{children}</main>
      </div>
    </div>
  );
};

export default AdminLayout;
